import { zodResolver } from "@hookform/resolvers/zod";
import { type CommunityContactFormPayload, ContactSubject, communityContactFormSchema } from "@upsquad/shared";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { LegalFooter } from "@/src/common/components/legal-footer";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Button, FieldError, Input, Label, ListBox, Select, Spinner, TextArea, TextField } from "@/src/common/components/ui";
import { getSubdomain } from "@/src/common/lib/subdomain";
import NotFoundPage from "@/src/common/pages/not-found-page";
import { useGetPublicCommunityMobileAppConfigQuery, useGetPublicCommunityQuery } from "@/src/common/services/community-public-http.service";
import { toastService } from "@/src/common/services/toast.service";
import { PublicCommunityHeader } from "@/src/community/components/public-community-header";
import { useSubmitCommunityContactMutation } from "@/src/contact/services/contact-http.service";

const SUBJECT_OPTIONS: ContactSubject[] = [
	ContactSubject.GENERAL,
	ContactSubject.FEATURE_REQUEST,
	ContactSubject.BUG_REPORT,
	ContactSubject.ACCOUNT_REMOVAL,
];

const isValidContactSubject = (value: string | null): value is ContactSubject => {
	if (!value) return false;
	return SUBJECT_OPTIONS.some((option) => option === value);
};

export default function PublicContactPage() {
	const { t } = useTranslation("contact");
	const subdomain = getSubdomain();
	const [searchParams] = useSearchParams();
	const { data, isLoading, isError } = useGetPublicCommunityMobileAppConfigQuery(subdomain ?? "", { skip: !subdomain });
	const { data: community } = useGetPublicCommunityQuery(subdomain ?? "", { skip: !subdomain });
	const [submitContact, { isLoading: isSubmitting }] = useSubmitCommunityContactMutation();

	const subjectFromUrl = searchParams.get("subject");
	const initialSubject = isValidContactSubject(subjectFromUrl) ? subjectFromUrl : ContactSubject.GENERAL;

	const { control, handleSubmit, reset } = useForm<CommunityContactFormPayload>({
		resolver: zodResolver(communityContactFormSchema),
		defaultValues: {
			name: "",
			email: "",
			subject: initialSubject,
			message: "",
		},
	});

	const onSubmit = async (values: CommunityContactFormPayload) => {
		if (!subdomain) return;
		try {
			await submitContact({ slug: subdomain, payload: values }).unwrap();
			toastService.success(t("submit_success"));
			reset();
		} catch (err) {
			if (typeof err === "object" && err !== null && "status" in err && err.status === 429) {
				toastService.error(t("submit_rate_limited"));
			} else {
				toastService.error(t("submit_error"));
			}
		}
	};

	if (!subdomain || isError) return <NotFoundPage />;

	if (isLoading || !data) {
		return (
			<>
				<PageHelmet title={t("loading")} />
				<div className="flex min-h-screen items-center justify-center">
					<Spinner />
				</div>
			</>
		);
	}

	return (
		<>
			<PageHelmet title={t("contact_title", { name: data.community_name })} />
			<div className="flex min-h-screen flex-col bg-background">
				{community && <PublicCommunityHeader communityName={community.name} communityImage={community.image ?? null} />}
				<main className="mx-auto w-full max-w-3xl flex-1 px-6 py-16">
					<h1 className="mb-4 text-4xl font-semibold text-foreground">{t("contact_title", { name: data.community_name })}</h1>
					<p className="mb-10 text-foreground/60">{t("page_intro")}</p>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
						<Controller
							control={control}
							name="name"
							render={({ field, fieldState }) => (
								<TextField isInvalid={!!fieldState.error} isRequired>
									<Label>{t("name_label")}</Label>
									<Input value={field.value} onChange={(e) => field.onChange(e.target.value)} onBlur={field.onBlur} name={field.name} />
									{fieldState.error?.message && <FieldError>{fieldState.error.message}</FieldError>}
								</TextField>
							)}
						/>

						<Controller
							control={control}
							name="email"
							render={({ field, fieldState }) => (
								<TextField isInvalid={!!fieldState.error} isRequired>
									<Label>{t("email_label")}</Label>
									<Input
										type="email"
										value={field.value}
										onChange={(e) => field.onChange(e.target.value)}
										onBlur={field.onBlur}
										name={field.name}
									/>
									{fieldState.error?.message && <FieldError>{fieldState.error.message}</FieldError>}
								</TextField>
							)}
						/>

						<Controller
							control={control}
							name="subject"
							render={({ field, fieldState }) => (
								<Select
									isInvalid={!!fieldState.error}
									isRequired
									placeholder={t("subject_placeholder")}
									value={field.value}
									onChange={(value) => {
										if (value !== null) field.onChange(value);
									}}
									name={field.name}
								>
									<Label>{t("subject_label")}</Label>
									<Select.Trigger>
										<Select.Value />
										<Select.Indicator />
									</Select.Trigger>
									<Select.Popover>
										<ListBox>
											{SUBJECT_OPTIONS.map((subject) => (
												<ListBox.Item key={subject} id={subject} textValue={t(`subject_${subject}`)}>
													{t(`subject_${subject}`)}
													<ListBox.ItemIndicator />
												</ListBox.Item>
											))}
										</ListBox>
									</Select.Popover>
									{fieldState.error?.message && <FieldError>{fieldState.error.message}</FieldError>}
								</Select>
							)}
						/>

						<Controller
							control={control}
							name="message"
							render={({ field, fieldState }) => (
								<TextField isInvalid={!!fieldState.error} isRequired>
									<Label>{t("message_label")}</Label>
									<TextArea
										rows={6}
										value={field.value}
										onChange={(e) => field.onChange(e.target.value)}
										onBlur={field.onBlur}
										name={field.name}
									/>
									{fieldState.error?.message && <FieldError>{fieldState.error.message}</FieldError>}
								</TextField>
							)}
						/>

						<div className="flex justify-end">
							<Button type="submit" variant="primary" isPending={isSubmitting}>
								{t("submit")}
							</Button>
						</div>
					</form>
				</main>
				<LegalFooter communityName={community?.name} />
			</div>
		</>
	);
}
